import { useEffect, useMemo, useRef, useState } from "react";

import { useLeadContext } from "../../../contexts/LeadContext";
import SearchInput from "../../../ui/inputs/SearchInput";
import LoadingScreen from "../../../ui/loadingScreen/LoadingScreen";
import CommunicationCard from "./CommunicationCard";

import styles from "./LeadCommunicationsPanel.module.css";

const LeadCommunicationsPanel = ({
  leadId,
  recipient,
  initialSubjectGroupId = null,
  onNewMail,
  onThreadSelected,
  onUnreadChange,
  withChannelTabs = false,
}) => {
  const {
    communicationSubjects,
    communicationSubjectsLoading,
    communicationSubjectsError,
    fetchCommunicationSubjects,
    selectedCommunicationSubjectId,
    setSelectedCommunicationSubjectId,
    setSelectedCommunicationSubject,
    setCommunicationSubjects,
    setConversationMessages,
    setConversationParticipants,
    fetchConversationMessages,
  } = useLeadContext();

  const [search, setSearch] = useState("");
  const autoSelectedLeadRef = useRef(null);
  const reportedUnreadRef = useRef(null);
  const loadingLeadRef = useRef(null);

  useEffect(() => {
    autoSelectedLeadRef.current = null;
    setSelectedCommunicationSubjectId?.(null);
    setSelectedCommunicationSubject?.(null);
    setCommunicationSubjects?.([]);
    setConversationMessages?.([]);
    setConversationParticipants?.([]);
  }, [
    leadId,
    setSelectedCommunicationSubjectId,
    setSelectedCommunicationSubject,
    setCommunicationSubjects,
    setConversationMessages,
    setConversationParticipants,
  ]);

  useEffect(() => {
    if (!leadId) return;

    fetchCommunicationSubjects?.({
      lead_id: leadId,
    });
  }, [fetchCommunicationSubjects, leadId]);

  useEffect(() => {
    if (communicationSubjectsLoading) loadingLeadRef.current = leadId;
  }, [communicationSubjectsLoading, leadId]);

  useEffect(() => {
    if (!leadId) return;
    if (communicationSubjectsLoading) return;
    if (loadingLeadRef.current !== leadId) return;
    if (communicationSubjectsError) return;
    if (!Array.isArray(communicationSubjects)) return;

    const hasUnread = communicationSubjects.some(
      (communication) => communication?.has_unread,
    );
    const reported = reportedUnreadRef.current;

    if (reported?.leadId === leadId && reported?.hasUnread === hasUnread) return;

    reportedUnreadRef.current = { leadId, hasUnread };
    onUnreadChange?.(hasUnread);
  }, [
    leadId,
    communicationSubjects,
    communicationSubjectsLoading,
    communicationSubjectsError,
    onUnreadChange,
  ]);

  const filteredCommunications = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    if (!normalizedSearch) return communicationSubjects || [];

    return (communicationSubjects || []).filter((communication) => {
      const subjectGroupName =
        communication?.subject_group_name ||
        communication?.subject_group ||
        communication?.normalized_subject ||
        communication?.subject ||
        "";

      return subjectGroupName.toLowerCase().includes(normalizedSearch);
    });
  }, [communicationSubjects, search]);

  useEffect(() => {
    if (!leadId) return;
    if (selectedCommunicationSubjectId) return;
    if (!communicationSubjects?.length) return;
    if (autoSelectedLeadRef.current === leadId) return;

    const initialCommunication =
      initialSubjectGroupId != null
        ? communicationSubjects.find(
            (communication) =>
              Number(communication?.subject_group_id ?? communication?.id) ===
              Number(initialSubjectGroupId),
          )
        : null;

    const communicationToSelect =
      initialCommunication ?? communicationSubjects[0];
    const subjectGroupId =
      communicationToSelect?.subject_group_id ?? communicationToSelect?.id;

    if (!subjectGroupId) return;

    autoSelectedLeadRef.current = leadId;
    setSelectedCommunicationSubjectId?.(subjectGroupId);
    setSelectedCommunicationSubject?.(communicationToSelect);

    fetchConversationMessages?.({
      lead_id: leadId,
      subject_group_id: subjectGroupId,
    });
  }, [
    leadId,
    communicationSubjects,
    selectedCommunicationSubjectId,
    initialSubjectGroupId,
    setSelectedCommunicationSubjectId,
    setSelectedCommunicationSubject,
    fetchConversationMessages,
  ]);

  const handleSelectCommunication = (communication) => {
    const subjectGroupId = communication?.subject_group_id ?? communication?.id;

    setSelectedCommunicationSubjectId?.(subjectGroupId);
    setSelectedCommunicationSubject?.(communication);

    fetchConversationMessages?.({
      lead_id: leadId,
      subject_group_id: subjectGroupId,
    });

    onThreadSelected?.();
  };

  const handleNewMail = () => {
    autoSelectedLeadRef.current = leadId;
    setSelectedCommunicationSubjectId?.(null);
    setSelectedCommunicationSubject?.(null);
    setConversationMessages?.([]);
    setConversationParticipants?.([]);
    onNewMail?.();
  };

  return (
    <section
      className={`${styles.leadCommunicationsPanel} ${
        withChannelTabs ? styles.withChannelTabs : ""
      }`}
    >
      <div className={styles.searchContainer}>
        <SearchInput
          value={search}
          onChange={(value) => setSearch(value ?? "")}
          placeholder="Buscar..."
        />
      </div>

      <button
        type="button"
        className={styles.newMailButton}
        onClick={handleNewMail}
      >
        ＋ Nuevo correo
      </button>

      <div className={styles.communicationsList}>
        {communicationSubjectsLoading ? (
          <LoadingScreen
            embedded
            mainText="Cargando comunicaciones..."
            subText=""
          />
        ) : communicationSubjectsError ? (
          <div className={styles.emptyState}>
            No se pudieron cargar las comunicaciones.
          </div>
        ) : filteredCommunications.length === 0 ? (
          <div className={styles.emptyState}>
            No hay comunicaciones para mostrar.
          </div>
        ) : (
          filteredCommunications.map((communication) => {
            const subjectGroupId = communication?.subject_group_id ?? communication?.id;

            return (
              <CommunicationCard
                key={subjectGroupId}
                communication={communication}
                recipient={recipient}
                selected={selectedCommunicationSubjectId === subjectGroupId}
                onSelect={handleSelectCommunication}
              />
            );
          })
        )}
      </div>
    </section>
  );
};

export default LeadCommunicationsPanel;
